import { DocumentNode, useApolloClient } from '@apollo/client';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@components/Alert';
import {
  Document,
  DocumentAttributes,
  GetProjectDocument,
  GetTaskDocument,
  ListDraftProjectDocumentsDocument,
  ListDraftTaskDocumentsDocument,
  useAddProjectFilesMutation,
  useAddTaskFilesMutation,
  useListDraftProjectDocumentsQuery,
  useListDraftTaskDocumentsQuery,
} from '@graphql/generated';
import useFileProcessor from '@hooks/useFileProcessor';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import { useRateAppPrompt } from '@hooks/useRateAppPrompt';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';

import type { UploadProgressData } from 'expo-file-system/legacy';

type UploadingItem = {
  file: Document;
  progress: number;
};

type UploadFilesContext = {
  uploadingFiles: UploadingItem[];
  setUploadingFiles: React.Dispatch<React.SetStateAction<UploadingItem[]>>;
  uploadingErrorFiles: (Document | undefined)[];
  setUploadingErrorFiles: React.Dispatch<
    React.SetStateAction<(Document | undefined)[]>
  >;
  uploadingSuccessFiles: (Document | undefined)[];
  setUploadingSuccessFiles: React.Dispatch<
    React.SetStateAction<(Document | undefined)[]>
  >;
  lastSuccessBatch: Document[];
  setTaskId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setProjectId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setProjectUUId: React.Dispatch<React.SetStateAction<string | undefined>>;
  resendFiles: (documents: any[]) => Promise<void>;
  deleteFailedFiles: (documents: any[]) => void;
  draftData: Document[];
  setDraftData: React.Dispatch<React.SetStateAction<Document[]>>;
};

const uploadFilesContext = createContext<UploadFilesContext | undefined>(
  undefined
);

export const UploadFilesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { Provider } = uploadFilesContext;
  const { t } = useTranslation();
  const { trackEvent } = useMixpanel();
  const [uploadingFiles, setUploadingFiles] = useState<UploadingItem[]>([]);
  const [uploadingErrorFiles, setUploadingErrorFiles] = useState<
    (Document | undefined)[]
  >([]);
  const [uploadingSuccessFiles, setUploadingSuccessFiles] = useState<
    (Document | undefined)[]
  >([]);
  const [lastSuccessBatch, setLastSuccessBatch] = useState<Document[]>([]);
  const { recordTrigger: recordRateAppTrigger } = useRateAppPrompt();

  const [taskId, setTaskId] = useState<string | undefined>();
  const [projectId, setProjectId] = useState<string | undefined>();
  const [projectUUId, setProjectUUId] = useState<string | undefined>();
  const [draftData, setDraftData] = useState<Document[]>([]);

  const { checkClientIds } = useGetDocumentsByClientIdsQuery();

  const isProject = !!projectId;
  const queryDocument: DocumentNode = isProject
    ? ListDraftProjectDocumentsDocument
    : ListDraftTaskDocumentsDocument;

  const queryCacheKey = isProject
    ? 'listDraftProjectDocuments'
    : 'listDraftTaskDocuments';

  const queryVariables = useMemo(
    () => (isProject ? { projectId } : { taskId }),
    [projectId, taskId]
  );

  const client = useApolloClient();
  const draftRef = useRef<any[]>([]);

  const { data: draftProjectData } = useListDraftProjectDocumentsQuery({
    variables: { projectId: projectId || '' },
    fetchPolicy: 'cache-only',
    skip: !projectId,
  });

  const { data: draftTaskData } = useListDraftTaskDocumentsQuery({
    variables: { taskId: taskId || '' },
    fetchPolicy: 'cache-only',
    skip: !taskId,
  });

  const drafts = isProject
    ? draftProjectData?.listDraftProjectDocuments || []
    : draftTaskData?.listDraftTaskDocuments || [];

  const prevProjectId = useRef<string | undefined>();
  const prevTaskId = useRef<string | undefined>();

  useEffect(() => {
    const projectChanged =
      prevProjectId.current && prevProjectId.current !== projectId;
    const taskChanged = prevTaskId.current && prevTaskId.current !== taskId;

    if (projectChanged || taskChanged) {
      setUploadingErrorFiles([]);
      setUploadingSuccessFiles([]);
      setUploadingFiles([]);
      draftRef.current = [];
    }

    prevProjectId.current = projectId;
    prevTaskId.current = taskId;
  }, [projectId, taskId]);

  const doUploadFiles = (drafts: Document[], isDraftData: boolean) => {
    if (!drafts || drafts.length === 0) return;

    const newUploadableDrafts = drafts.filter(
      (d) => !d.failedAt && !draftRef.current.some((r) => r.id === d.id)
    );

    if (newUploadableDrafts.length > 0) {
      draftRef.current = [...draftRef.current, ...newUploadableDrafts];
      setUploadingFiles((prev: any) => {
        const existingIds = new Set(prev.map((item: any) => item.file.id));
        const newItems = newUploadableDrafts
          .filter((doc) => !existingIds.has(doc.id))
          .map((doc) => ({ file: doc, progress: 0 }));
        return [...prev, ...newItems];
      });

      uploadFiles(newUploadableDrafts);
    }

    const failedDrafts = drafts.filter((d) => d.failedAt != null);
    if (failedDrafts.length > 0) {
      setUploadingErrorFiles((prev: any) => {
        const existingIds = new Set(prev.map((doc: any) => doc?.id));
        const newDocs = failedDrafts.filter((doc) => !existingIds.has(doc.id));
        return [...prev, ...newDocs];
      });
    }
    isDraftData && setDraftData([]);
  };

  useEffect(() => {
    if (drafts && drafts.length > 0) {
      doUploadFiles(drafts, false);
    } else if (draftData && draftData.length > 0) {
      doUploadFiles(draftData, true);
    }
  }, [drafts, draftData]);

  useEffect(() => {
    if (lastSuccessBatch.length > 0) {
      const timeout = setTimeout(() => {
        setLastSuccessBatch([]);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [lastSuccessBatch]);

  const { processFiles } = useFileProcessor();

  const [addProjectFiles] = useAddProjectFilesMutation({
    refetchQueries: [
      'listProjectDocuments',
      ...(projectUUId
        ? [{ query: GetProjectDocument, variables: { id: projectUUId } }]
        : []),
    ],
  });

  const [addTaskFiles] = useAddTaskFilesMutation({
    refetchQueries: [
      'listTaskDocuments',
      { query: GetTaskDocument, variables: { id: taskId } },
    ],
  });

  const callback = (
    documentClientId: string,
    uploadProgress: UploadProgressData
  ) => {
    const progress =
      uploadProgress.totalBytesSent / uploadProgress.totalBytesExpectedToSend;

    setUploadingFiles((prev) =>
      prev.map((item) =>
        item.file.clientId === documentClientId ? { ...item, progress } : item
      )
    );
  };

  const handleCompletedFiles = async (documents: any[]) => {
    const idsToRemove = documents.map((d) => d.id);
    client.cache.updateQuery(
      {
        query: queryDocument,
        variables: queryVariables,
      },
      (data) => {
        const currentList = data?.[queryCacheKey] || [];
        return {
          [queryCacheKey]: currentList.filter(
            (item: any) => !idsToRemove.includes(item.id)
          ),
        };
      }
    );
  };

  const handleFailedFiles = async (documents: any[]) => {
    const now = new Date().toISOString();
    const failedMap = new Map(documents.map((d) => [d.id, true]));

    client.cache.updateQuery(
      {
        query: queryDocument,
        variables: queryVariables,
      },
      (data) => {
        const currentList = data?.[queryCacheKey] || [];
        return {
          [queryCacheKey]: currentList.map((item: any) => {
            if (failedMap.has(item.id)) {
              return {
                ...item,
                failedAt: now,
              };
            }
            return item;
          }),
        };
      }
    );
  };

  const resendFiles = async (documents: any[]) => {
    const idsToResend = new Set(documents.map((d) => d.id));

    client.cache.updateQuery(
      {
        query: queryDocument,
        variables: queryVariables,
      },
      (data) => {
        const currentList = data?.[queryCacheKey] || [];
        return {
          [queryCacheKey]: currentList.map((item: any) => {
            if (idsToResend.has(item.id)) {
              return {
                ...item,
                failedAt: null,
              };
            }
            return item;
          }),
        };
      }
    );

    setUploadingErrorFiles((prev) =>
      prev.filter((doc) => !idsToResend.has(doc?.id))
    );

    draftRef.current = draftRef.current.filter(
      (doc) => !idsToResend.has(doc.id)
    );
  };

  const deleteFailedFiles = (documents: any[]) => {
    const idsToDelete = new Set(documents.map((d) => d.id));
    client?.cache.updateQuery(
      {
        query: queryDocument,
        variables: queryVariables,
      },
      (data) => {
        const currentList = data?.[queryCacheKey] || [];
        return {
          [queryCacheKey]: currentList.filter(
            (item: any) => !idsToDelete.has(item.id)
          ),
        };
      }
    );
  };

  const uploadFiles = async (documents: any[]) => {
    if (documents.length === 0) return;

    try {
      const response = await processFiles(documents, callback);

      const addFileFunc = projectId ? addProjectFiles : addTaskFiles;

      const attributes = documents.map((item) => {
        const {
          name,
          clientId,
          contentType,
          isImage = false,
          isAudio = false,
        } = item;

        const matched = response.find((r) => r.clientId === clientId);
        const blobId = matched?.blobId || '';

        if (!blobId) {
          console.warn(`⚠️ Missing blobId for file: ${name}`);
        }

        return {
          name,
          contentType,
          isImage,
          isAudio,
          blobId,
          clientId,
        } as DocumentAttributes;
      });

      addFileFunc({
        variables: {
          id: projectId ?? taskId ?? '',
          attributes,
        },
        onCompleted: () => {
          checkClientIds(response.map((newItem) => newItem.clientId));

          documents.forEach((doc) => {
            trackEvent(MIXPANEL_EVENTS.UPLOADED_DOCUMENT, {
              project_id: projectId,
              task_id: taskId,
              document_id: doc.id,
              document_type: doc.contentType,
            });
          });

          setUploadingSuccessFiles((prev) => [
            ...prev,
            ...documents.filter((doc) => !prev.some((d) => d?.id === doc.id)),
          ]);
          setUploadingFiles((prev) =>
            prev.filter((item) => !documents.some((d) => d.id === item.file.id))
          );
          setLastSuccessBatch(documents);
          handleCompletedFiles(documents);
          // One successful upload action counts once toward the rate-app
          // prompt, regardless of how many files were in the batch
          recordRateAppTrigger('upload_media');
        },
        onError: (e) => {
          if (e.message === t('models:projects.files.storageLimitReached')) {
            Alert.alert(
              'TaskTag',
              t('models:projects.files.storageLimitReached')
            );
            setUploadingFiles([]);
            handleCompletedFiles(documents);
            return;
          }
          setUploadingErrorFiles((prev) => [
            ...prev,
            ...documents.filter((doc) => !prev.some((d) => d?.id === doc.id)),
          ]);
          setUploadingFiles((prev) =>
            prev.filter((item) => !documents.some((d) => d.id === item.file.id))
          );
          handleFailedFiles(documents);
        },
      });
    } catch (err) {
      console.error('processFiles failed', err);
      setUploadingErrorFiles((prev) => [
        ...prev,
        ...documents.filter((doc) => !prev.some((d) => d?.id === doc.id)),
      ]);
      setUploadingFiles((prev) =>
        prev.filter((item) => !documents.some((d) => d.id === item.file.id))
      );
      handleFailedFiles(documents);
    }
  };

  return (
    <Provider
      value={{
        uploadingFiles,
        setUploadingFiles,
        uploadingErrorFiles,
        setUploadingErrorFiles,
        uploadingSuccessFiles,
        setUploadingSuccessFiles,
        lastSuccessBatch,
        setTaskId,
        setProjectId,
        setProjectUUId,
        resendFiles,
        deleteFailedFiles,
        draftData,
        setDraftData,
      }}>
      {children}
    </Provider>
  );
};

export const useUploadFiles = (): UploadFilesContext => {
  const context = useContext(uploadFilesContext);
  if (context === undefined) {
    throw new Error('useUploadFiles must be used within a Provider');
  }
  return context;
};
